var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { Maximize, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button } from '../../ui/button.jsx';
import { Slider } from '../../ui/slider.jsx';
import { cn } from '../../../lib/utils.js';
/**
 * HTML5 video/audio player for legacy media playback.
 *
 * @deprecated Use `MuxVideoPlayer` from `@govinity/ui/components/modules/mux-video-player` instead.
 * This component is maintained for backwards compatibility with legacy S3/CloudFront recordings
 * that don't have a Mux playback ID. New recordings should use Mux exclusively.
 */
export var MediaPlayer = forwardRef(function (_a, ref) {
    var src = _a.src, poster = _a.poster, onTimeUpdate = _a.onTimeUpdate, className = _a.className;
    var _b = useState(false), isPlaying = _b[0], setIsPlaying = _b[1];
    var _c = useState(0), currentTime = _c[0], setCurrentTime = _c[1];
    var _d = useState(0), duration = _d[0], setDuration = _d[1];
    var _e = useState(1), volume = _e[0], setVolume = _e[1];
    var _f = useState(false), isMuted = _f[0], setIsMuted = _f[1];
    var mediaRef = useRef(null);
    var isAudioFromSrc = function (url) {
        var _a, _b;
        if (url === void 0) { url = ''; }
        var lower = (_b = (_a = url.split('?')[0]) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : '';
        return lower.endsWith('.mp3') || lower.endsWith('.wav') || lower.endsWith('.ogg');
    };
    var isAudio = isAudioFromSrc(src);
    useEffect(function () {
        // Pause on unmount
        var media = mediaRef.current;
        return function () {
            if (media)
                media.pause();
        };
    }, []);
    var handlePlayPause = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!mediaRef.current) return [3 /*break*/, 7];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 6, , 7]);
                    if (!isPlaying) return [3 /*break*/, 3];
                    return [4 /*yield*/, mediaRef.current.pause()];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 3: return [4 /*yield*/, mediaRef.current.play()];
                case 4:
                    _a.sent();
                    _a.label = 5;
                case 5:
                    setIsPlaying(!isPlaying);
                    return [3 /*break*/, 7];
                case 6:
                    error_1 = _a.sent();
                    console.error('Error playing/pausing media:', error_1);
                    // Reset playing state if there was an error
                    setIsPlaying(false);
                    return [3 /*break*/, 7];
                case 7: return [2 /*return*/];
            }
        });
    }); }, [isPlaying]);
    var handleTimeUpdate = useCallback(function () {
        if (mediaRef.current) {
            var time = mediaRef.current.currentTime || 0;
            setCurrentTime(time);
            onTimeUpdate === null || onTimeUpdate === void 0 ? void 0 : onTimeUpdate(time);
        }
    }, [onTimeUpdate]);
    var handleLoadedMetadata = useCallback(function () {
        if (mediaRef.current) {
            var mediaDuration = mediaRef.current.duration || 0;
            setDuration(mediaDuration);
        }
    }, []);
    var handleSeek = useCallback(function (value) {
        var newTime = value[0] || 0;
        if (mediaRef.current) {
            mediaRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    }, []);
    var handleVolumeChange = useCallback(function (value) {
        var newVolume = value[0] || 0;
        if (mediaRef.current) {
            mediaRef.current.volume = newVolume;
            setVolume(newVolume);
            setIsMuted(newVolume === 0);
        }
    }, []);
    var toggleMute = useCallback(function () {
        if (mediaRef.current) {
            var newMutedState = !isMuted;
            mediaRef.current.muted = newMutedState;
            setIsMuted(newMutedState);
        }
    }, [isMuted]);
    var handleFullscreen = useCallback(function () {
        var media = mediaRef.current;
        if (!media || isAudio)
            return;
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(function (err) { return console.error('Error exiting fullscreen:', err); });
        }
        else {
            media
                .requestFullscreen()
                .catch(function (err) { return console.error('Error entering fullscreen:', err); });
        }
    }, [isAudio]);
    var formatTime = function (time) {
        var minutes = Math.floor(time / 60);
        var seconds = Math.floor(time % 60);
        return "".concat(minutes, ":").concat(seconds.toString().padStart(2, '0'));
    };
    // Expose seek method via ref
    useImperativeHandle(ref, function () { return ({
        seek: function (time) {
            var media = mediaRef.current;
            if (media) {
                // Ensure time is within valid range
                var clampedTime = Math.max(0, Math.min(time, media.duration || time));
                media.currentTime = clampedTime;
                setCurrentTime(clampedTime);
                // Notify parent of time change so activeSegmentIndex updates
                onTimeUpdate === null || onTimeUpdate === void 0 ? void 0 : onTimeUpdate(clampedTime);
            }
        },
    }); }, [onTimeUpdate]);
    // Don't render until we have a media URL
    if (!src) {
        return null;
    }
    return (<div className="space-y-4">
        <div className={cn('bg-muted relative aspect-video overflow-hidden', className)}>
          {isAudio ? (<>
              <audio ref={mediaRef} src={src} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata}>
                <track kind="captions" label="Captions"/>
              </audio>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-muted-foreground text-2xl">Audio Recording</div>
              </div>
            </>) : (<video ref={mediaRef} src={src} poster={poster} className="h-full w-full" onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata}>
              <track kind="captions" label="Captions"/>
            </video>)}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={handlePlayPause} aria-label={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? <Pause className="h-4 w-4"/> : <Play className="h-4 w-4"/>}
            </Button>
            <span className="text-muted-foreground min-w-[4rem] text-sm">
              {formatTime(currentTime)}
            </span>
            <Slider value={[currentTime]} min={0} max={duration} step={0.1} onValueChange={handleSeek} className="flex-1" aria-label="Seek"/>
            <span className="text-muted-foreground min-w-[4rem] text-right text-sm">
              {formatTime(duration)}
            </span>
            {!isAudio && (<Button variant="ghost" size="icon" onClick={handleFullscreen} aria-label="Fullscreen">
                <Maximize className="h-4 w-4"/>
              </Button>)}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleMute} aria-label={isMuted ? 'Unmute' : 'Mute'}>
              {isMuted ? <VolumeX className="h-4 w-4"/> : <Volume2 className="h-4 w-4"/>}
            </Button>
            <Slider value={[volume]} min={0} max={1} step={0.01} onValueChange={handleVolumeChange} className="w-[100px]" aria-label="Volume"/>
          </div>
        </div>
      </div>);
});
MediaPlayer.displayName = 'MediaPlayer';
